"use client"

import React, { useState, useEffect, useMemo } from "react"
import { usePathname } from "next/navigation"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { trackContact } from "@/lib/meta-pixel"
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Languages,
  ChevronRight,
  BookOpen,
  GraduationCap,
  Calendar,
  MessageCircle,
  Building2,
  Globe,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  CircleDollarSign,
  CreditCard,
  Stamp,
  ShieldCheck,
  Plane,
  Users,
} from "lucide-react"
import { useI18n } from "@/context/i18n"
import { pickLocalized } from "@/lib/i18nUtils"
import AnniversaryLogo from "./AnniversaryLogo"
import { useNavbarEffects } from "@/hooks/useNavbarEffects"
import BackgroundLottie from "./BackgroundLottie"

export default function UnifiedNavbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [activeServiciosSub, setActiveServiciosSub] = useState<string | null>(null)
  const { lang, setLang, t } = useI18n()
  const { config: effectsConfig } = useNavbarEffects()
  const showBgLottie = effectsConfig.active && effectsConfig.bgLottieEnabled
  const bgLottieUrl = scrolled ? effectsConfig.bgLottieScrolledUrl : effectsConfig.bgLottieTransparentUrl

  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [pagosMenuOpen, setPagosMenuOpen] = useState(false)
  const [hash, setHash] = useState<string>(typeof window !== 'undefined' ? window.location.hash : '')
  const routePathname = usePathname()
  const pathname = routePathname || (typeof window !== 'undefined' ? window.location.pathname : '')

  // Track hash for in-page anchors
  useEffect(() => {
    const update = () => setHash(window.location.hash)
    update()
    window.addEventListener('hashchange', update)
    return () => window.removeEventListener('hashchange', update)
  }, [])

  const isActive = (href: string) => {
    if (!href) return false
    if (href.startsWith('#')) return hash === href
    // Treat Servicios parent as active for any /servicios/* path
    if (href.startsWith('/servicios/')) {
      return pathname.startsWith('/servicios/')
    }
    try {
      return pathname.startsWith(href)
    } catch {
      return false
    }
  }

  const logoLight = "/logos/Logo_black.png"
  const logoDark = "/logos/Logo_white.png"

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setScrolled(scrollPosition > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close dropdowns when clicking outside (desktop only). Disabled while mobile menu is open.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen) return // don't interfere with mobile accordion logic
      const target = event.target as Element
      if (!target.closest('.nav-dropdown')) {
        setActiveDropdown(null)
      }
      // close language dropdown when clicking outside
      if (!target.closest('.lang-dropdown')) {
        setLangMenuOpen(false)
      }
      // close pagos dropdown when clicking outside
      if (!target.closest('.pagos-dropdown')) {
        setPagosMenuOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isMenuOpen])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const original = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = original
      }
    }
  }, [isMenuOpen])

  const LANG_ORDER: Array<'ES' | 'EN' | 'FR'> = ['ES', 'EN', 'FR']
  const langLabel = (l: 'ES' | 'EN' | 'FR') => l === 'ES' ? 'Español' : l === 'EN' ? 'English' : 'Français'
  const toggleLang = () => {
    const idx = LANG_ORDER.indexOf(lang as any)
    const next = LANG_ORDER[(idx + 1) % LANG_ORDER.length]
    setLang(next)
  }

  // --- WhatsApp CTA: compute same contextual message as floating chat ---
  const WHATSAPP_PHONE = "+5217122315859"
  const phoneDigits = useMemo(() => WHATSAPP_PHONE.replace(/[^\d]/g, ""), [])

  const contextualMessage = useMemo(() => {
    const raw = (routePathname || "/").toLowerCase()
    let path = raw.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, "")
    if (path === "") path = "/"
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1)

    const url = typeof window !== "undefined" ? window.location.href : undefined
    const title = typeof document !== "undefined" ? document.title : undefined

    if (path === "/servicios" || path.startsWith("/servicios/")) {
      const parts = path.split('/').filter(Boolean)
      if (parts[0] === 'servicios' && parts[1]) {
        const slug = parts[1]
        const msgKey = `joinchat_service_${slug}_message`
        const msg = t(msgKey)
        if (msg !== msgKey) return msg
      }
      return t('joinchat_root_message')
    }

    const regexRules: Array<[RegExp, string]> = [
      [/^\/categoria\/(study|estudio)/, t('joinchat_category_study_message')],
      [/^\/categoria\/(travel|viaje)/, t('joinchat_category_travel_message')],
      [/^\/categoria\/(participaciones)/, t('joinchat_promotions_message')],
      [/^\/servicios\/(lets-study|study)/, t('joinchat_category_study_message')],
      [/^\/servicios\/(lets-travel|travel)/, t('joinchat_category_travel_message')],
      [/^\/blog(\/|$)/, t('joinchat_root_message')],
      [/^\/seccion(\/|$)/, t('joinchat_root_message')],
      [/^\/tipo-cambio(\/|$)/, t('joinchat_tipo_cambio_message')],
    ]
    for (const [re, msg] of regexRules) {
      if (re.test(path)) return msg
    }

    const prefixRules: Array<[string, string]> = [
      ["/nosotros", t('joinchat_root_message')],
      ["/promociones", t('joinchat_promotions_message')],
      ["/bolsa_trabajo", t('joinchat_root_message')],
      ["/podcast", t('joinchat_podcast_message')],
      ["/divisas", t('joinchat_root_message')],
      ["/tipo-cambio", t('joinchat_tipo_cambio_message')],
      ["/categoria", t('joinchat_root_message')],
      ["/servicios", t('joinchat_root_message')],
    ]
    for (const [prefix, msg] of prefixRules) {
      if (path === prefix || path.startsWith(prefix + "/")) return msg
    }

    if (path === "/") {
      return t('joinchat_root_message')
    }

    const tail = url ? `: ${url}` : (title ? ` (${title})` : ".")
    return `${t('joinchat_root_message')}${tail}`
  }, [routePathname, lang, t])

  const waHref = useMemo(() => {
    const encodedMsg = encodeURIComponent(contextualMessage)
    return `https://api.whatsapp.com/send?phone=${phoneDigits}&text=${encodedMsg}`
  }, [phoneDigits, contextualMessage])

  const handleWhatsAppClick = () => {
    if (typeof window !== 'undefined') {
      trackContact('whatsapp', { content_type: 'navbar' })
      window.open(waHref, '_blank', 'noopener,noreferrer')
    }
  }

  const mainNavItems = [
    {
      name: t('nav_nosotros'),
      href: "/nosotros",
      icon: Building2,
      description: t('submenu_historia')
    },
    {
      name: t('nav_promociones'),
      href: "/promociones",
      icon: Sparkles
    },
    {
      name: t('nav_servicios'),
      href: "/servicios/estudios-en-el-extranjero",
      icon: Plane,
      description: t('submenu_servicios_desc'),
      submenu: [
        {
          name: t('nav_lets_study'),
          description: t('nav_lets_study_desc'),
          href: "/servicios/lets-study",
          icon: <GraduationCap className="w-4 h-4 text-[#fb0009]" />,
          children: [
            { name: t('lets_study_children_educational_groups'), href: "/servicios/grupos-educativos" },
            { name: t('lets_study_children_languages'), href: "/servicios/idiomas" },
            { name: t('lets_study_children_work_and_study'), href: "/servicios/estudia-y-trabaja" },
            { name: t('lets_study_children_highschool'), href: "/servicios/highschool" },
            { name: t('lets_study_children_aupair'), href: "/servicios/aupair" },
            { name: t('lets_study_children_pathway'), href: "/servicios/pathway" },
            { name: t('lets_study_children_higher_education'), href: "/servicios/educacion-superior" },
            { name: t('lets_study_children_online_english'), href: "/servicios/ingles-online" },
          ]
        },
        {
          name: t('nav_lets_travel'),
          description: t('nav_lets_travel_desc'),
          href: "/servicios/lets-travel",
          icon: <Globe className="w-4 h-4 text-[#fb0009]" />,
          children: [
            { name: t('lets_travel_children_international_travel'), href: "/servicios/viajes-internacionales" },
            { name: t('lets_travel_children_domestic_travel'), href: "/servicios/viajes-nacionales" },
            { name: t('lets_travel_children_bachelor_parties'), href: "/servicios/despedida-de-solteros" },
            { name: t('lets_travel_children_honeymoons'), href: "/servicios/lunas-de-miel" },
            { name: t('lets_travel_children_cruises'), href: "/servicios/cruceros" },
            { name: t('lets_travel_children_quinceaneras'), href: "/servicios/quinceaneras" },
            { name: t('lets_travel_children_destination_weddings'), href: "/servicios/bodas-destino" },
            { name: t('lets_travel_children_personalized_trips'), href: "/servicios/viajes-personalizados" },
            { name: t('lets_travel_children_corporate_travel'), href: "/servicios/turismo-corporativos" },
          ]
        },
        {
          name: t('nav_lets_inmigrate'),
          description: t('nav_lets_inmigrate_desc'),
          href: "/servicios/lets-inmigrate",
          icon: <BookOpen className="w-4 h-4 text-[#fb0009]" />,
          children: [
            { name: t('lets_inmigrate_children_canada'), href: "/servicios/migracion-canada" },
            { name: t('lets_inmigrate_australia'), href: "/categoria/participaciones/australia" },
            { name: t('lets_travel_children_visa_americana'), href: "/servicios/visa-americana" },
            { name: t('lets_travel_children_visa_canadiense'), href: "/servicios/visa-canadiense" },
            { name: t('lets_travel_children_eta_uk'), href: "/servicios/eta-uk" },
            { name: t('lets_travel_children_eta_canadiense'), href: "/servicios/eta-canadiense" },
            { name: t('lets_travel_children_other'), href: "/servicios/otros-tipos" },
          ]
        },
        {
          name: t('lets_study_children_educational_groups'),
          description: pickLocalized(lang, 'Campamentos en el extranjero', 'Educational Camps Abroad', 'Camps éducatifs à l\'étranger'),
          href: "/servicios/grupos-educativos",
          icon: <Users className="w-4 h-4 text-[#fb0009]" />,
        },
      ]
    },
    {
      name: t('nav_recursos'),
      href: "#recursos",
      icon: BookOpen,
      description: t('submenu_recursos_desc'),
      submenu: [
        {name: pickLocalized(lang, 'Acceso a Padres', 'Parent Access', 'Accès parents'), externalHref: "https://acceso-padres.grupo-let.com/login"},
        { name: pickLocalized(lang, 'Tutorial Acceso a Padres', 'Parent Access Tutorial', 'Tutoriel Accès parents'), href: "/servicios/grupos-educativos/tutorial-acceso-padres" },
        { name: pickLocalized(lang, 'Noticias, Blog y Webinars', 'News, Blog and Webinars', 'Nouvelles, Blog et Webinaires'), href: "/blog" },
        { name: t('nav_divisas'), href: "/divisas" },
        { name: 'Podcast', href: "/podcast" },
        { name: pickLocalized(lang, 'Bolsa de Trabajo', 'Job board', 'Bourse d\'emploi'), href: "/bolsa_trabajo" },
        { name: pickLocalized(lang, 'Mapa de Destinos', 'Destinations Map', 'Carte des destinations'), href: "/mapa-destinos" },
        { name: pickLocalized(lang, 'Colegios asociados', 'Partner Schools', 'Ecoles partenaires'), href: "/categoria/participaciones/colegios" },
        {
          name: t('nav_participations'),
          href: "/categoria/participaciones",
        },
      ]
    }
  ]

  const socialLinks = [
    { href: "https://www.facebook.com/LinkEducationTravel", label: "Facebook", icon: Facebook },
    { href: "https://www.instagram.com/linkeducationandtravel/?hl=es-la", label: "Instagram", icon: Instagram },
    { href: "https://www.youtube.com/@linkeducationandtravel", label: "YouTube", icon: Youtube },
    { href: "https://ca.linkedin.com/company/linkeducationca", label: "LinkedIn", icon: Linkedin },
  ] as const

  const handleDropdownToggle = (itemName: string) => {
    setActiveDropdown(activeDropdown === itemName ? null : itemName)
  }

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <>
      {/* Top Bar */}


      {/* Main Navbar */}
      <header className={cn(
        // Always include border-b to keep header box metrics stable and prevent any subtle shifts
        "fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b",
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-gray-200 shadow-md"
          : "bg-black border-transparent"
      )}>
        {/* Background Lottie Layer */}
        {showBgLottie && bgLottieUrl && (
          <BackgroundLottie
            url={bgLottieUrl}
            opacity={effectsConfig.bgLottieOpacity / 100}
          />
        )}

        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8">
          <div className={cn(
            "relative grid grid-cols-[auto_1fr_auto] items-center h-14 sm:h-16 md:h-18 lg:h-20 gap-x-2 sm:gap-x-3 lg:gap-x-4 xl:gap-x-6 transition-all duration-500"
          )}
          >
            <link rel="icon" type="image/png" sizes="64x64" href="favicon.ico" />

            {/* Logo */}
            <div className="flex items-center gap-4">
              <div className="h-8 lg:h-9 flex items-center max-w-[180px] lg:max-w-[200px] flex-shrink-0">
                <div className="w-full max-w-[140px] lg:max-w-[160px]">
                  <AnniversaryLogo lightSrc={logoLight} darkSrc={logoDark} scrolled={scrolled} />
                </div>
              </div>
            </div>

            {/* Desktop Navigation (centered) */}
            {/* CHANGELOG: nav solo aparece en lg o mayor */}
            <nav className="hidden lg:flex items-center justify-center w-full min-w-0 flex-1">
              {/* compact center nav: no horizontal scroll, smaller gaps and icons */}
              <div className="flex flex-1 items-center space-x-0 xl:space-x-1 min-w-0">
                {mainNavItems.map((item) => {
                  const active = isActive(item.href)
                  return (
                    <div key={item.name} className="relative nav-dropdown">
                      {item.submenu ? (
                        <button
                          onClick={() => handleDropdownToggle(item.name)}
                          className={cn(
                            "flex items-center gap-0.5 lg:gap-1 px-1 lg:px-2 xl:px-3 py-1 lg:py-2 rounded-xl font-bold text-xs lg:text-sm uppercase tracking-wide transition-all duration-300 group relative",
                            active ? "text-[#fb0009]" : (scrolled ? "text-black" : "text-white"),
                            scrolled ? "hover:text-[#fb0009] hover:bg-[#fb0009]/5" : "hover:text-[#fb0009] hover:bg-white/10"
                          )}
                          aria-haspopup="menu"
                          aria-expanded={activeDropdown === item.name}
                        >
                          <item.icon className="hidden 2xl:inline-block w-4 h-4 md:w-4 md:h-4 lg:w-4 lg:h-4" />
                          <span className="hidden lg:inline-block truncate max-w-[5.5rem] xl:max-w-[7.5rem]">{item.name}</span>
                          <ChevronDown className={cn(
                            "w-3 h-3 xl:w-4 xl:h-4 transition-transform duration-300",
                            activeDropdown === item.name ? "rotate-180" : ""
                          )} />
                          <div className={cn(
                            "absolute inset-x-0 -bottom-1 h-0.5 bg-[#fb0009] transition-transform duration-300",
                            active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                          )} />
                        </button>
                      ) : (
                        <a
                          href={item.href}
                          className={cn(
                            "flex items-center gap-0.5 lg:gap-1 px-1 lg:px-2 xl:px-3 py-1 lg:py-2 rounded-xl font-bold text-xs lg:text-sm uppercase tracking-wide transition-all duration-300 group relative",
                            active ? "text-[#fb0009]" : (scrolled ? "text-black" : "text-white"),
                            scrolled ? "hover:text-[#fb0009] hover:bg-[#fb0009]/5" : "hover:text-[#fb0009] hover:bg-white/10"
                          )}
                          data-ga-action="nav_click"
                          data-ga-category="navigation"
                          data-ga-label={item.name}
                        >
                          <item.icon className="hidden 2xl:inline-block w-4 h-4 md:w-4 md:h-4 lg:w-4 lg:h-4" />
                          <span className="hidden lg:inline-block truncate max-w-[5.5rem] xl:max-w-[7.5rem]">{item.name}</span>
                          <div className={cn(
                            "absolute inset-x-0 -bottom-1 h-0.5 bg-[#fb0009] transition-transform duration-300",
                            active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                          )} />
                        </a>
                      )}

                      {/* Dropdown Menu */}
                      {item.submenu && activeDropdown === item.name && (
                        // CHANGELOG: Recursos dropdown anchors to the nav item (right-0); other dropdowns stay centered
                        <div className={cn(
                          item.href === '#recursos'
                            ? "absolute top-full right-0 mt-3 min-w-[260px] -translate-x-6 rounded-2xl border border-gray-200 bg-white shadow-[0_8px_32px_-4px_rgba(0,0,0,0.15)] ring-1 ring-black/5 overflow-visible animate-slide-down z-[9999]"
                            : "absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[260px] rounded-2xl border border-gray-200 bg-white shadow-[0_8px_32px_-4px_rgba(0,0,0,0.15)] ring-1 ring-black/5 overflow-visible animate-slide-down z-[9999]"
                        )}>
                          <div className="p-2">
                            <div className="px-3 py-2 text-[11px] font-semibold tracking-wide uppercase text-gray-500 flex items-center gap-2">
                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#fb0009]/10 text-[#fb0009]">
                                <item.icon className="w-3.5 h-3.5" />
                              </span>
                              {item.description}
                            </div>
                            <div className="relative divide-y divide-gray-100/60">
                              {(item.submenu as any[]).map((subItem: any, index: number) => (
                                <div key={index} className="group relative" onMouseLeave={() => setActiveServiciosSub(null)}>
                                  {(() => {
                                    const subHref = subItem.externalHref ?? subItem.href
                                    const isExternalSubLink = Boolean(subItem.externalHref)
                                    return (
                                      <>
                                  {subItem.children ? (
                                    <button
                                      type="button"
                                      className={cn(
                                        "w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                                        activeServiciosSub === subItem.name
                                          ? "bg-[#fb0009]/5 shadow-inner text-[#fb0009]"
                                          : "hover:bg-[#fb0009]/5 text-gray-800"
                                      )}
                                      data-ga-action="nav_expand"
                                      data-ga-category="navigation"
                                      data-ga-label={`submenu:${subItem.name}`}
                                      onMouseEnter={() => setActiveServiciosSub(subItem.name)}
                                      onClick={() => setActiveServiciosSub(prev => prev === subItem.name ? null : subItem.name)}
                                    >
                                      {subItem.icon && <span className="text-[#fb0009] flex items-center justify-center h-8 w-8 rounded-lg bg-[#fb0009]/10">{subItem.icon}</span>}
                                      <div className="flex-1 leading-tight">
                                        <div className="font-semibold text-sm">{subItem.name}</div>
                                        {subItem.description && (
                                          <div className="text-[11px] text-gray-500 mt-0.5">{subItem.description}</div>
                                        )}
                                      </div>
                                      <ChevronRight className={cn(
                                        "w-4 h-4 transition-transform",
                                        activeServiciosSub === subItem.name ? "translate-x-0.5 text-[#fb0009]" : "text-gray-400 group-hover:translate-x-0.5"
                                      )} />
                                    </button>
                                  ) : (
                                    <a
                                      href={subHref}
                                      target={isExternalSubLink ? "_blank" : undefined}
                                      rel={isExternalSubLink ? "noopener noreferrer" : undefined}
                                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#fb0009]/5 transition-colors text-gray-800"
                                      data-ga-action="nav_click"
                                      data-ga-category="navigation"
                                      data-ga-label={`submenu:${subItem.name}`}
                                      onMouseEnter={() => setActiveServiciosSub(subItem.name)}
                                    >
                                      {subItem.icon && <span className="text-[#fb0009] flex items-center justify-center h-8 w-8 rounded-lg bg-[#fb0009]/10">{subItem.icon}</span>}
                                      <div className="flex-1 leading-tight">
                                        <div className="font-semibold text-sm">{subItem.name}</div>
                                        {subItem.description && (
                                          <div className="text-[11px] text-gray-500 mt-0.5">{subItem.description}</div>
                                        )}
                                      </div>
                                    </a>
                                  )}
                                  {subItem.children && (
                                    <div
                                      className={cn(
                                        "absolute top-0 left-full pl-2 bg-white rounded-xl shadow-xl border border-gray-200 min-w-[260px] p-3 z-[9999] ring-1 ring-black/5",
                                        activeServiciosSub === subItem.name ? "block" : "hidden group-hover:block"
                                      )}
                                      onMouseEnter={() => setActiveServiciosSub(subItem.name)}
                                      onMouseLeave={() => setActiveServiciosSub(null)}
                                    >
                                      <div className="flex flex-col gap-1">
                                        {subItem.children.map((opt: any, i: number) => (
                                          <a
                                            key={i}
                                            href={opt.href}
                                            className="group/item flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-[#fb0009]/5 hover:text-[#fb0009] transition-colors"
                                            data-ga-action="nav_click"
                                            data-ga-category="navigation"
                                            data-ga-label={`submenu_option:${opt.name}`}
                                          >
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover/item:bg-[#fb0009] transition-colors" />
                                            {opt.name}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                      </>
                                    )
                                  })()}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </nav>

            {/* Mobile Menu Button (visible on small screens) */}
            <div className="flex lg:hidden items-center justify-end">
              <button
                className={cn(
                  "flex items-center justify-center h-full px-2 sm:px-3 rounded-xl transition-colors duration-300",
                  scrolled ? "hover:bg-gray-100" : "hover:bg-white/10"
                )}
                onClick={handleMenuToggle}
                aria-label={isMenuOpen ? t('menu_close_aria') : t('menu_open_aria')}
              >
                {isMenuOpen ? (
                  <X className={cn("h-5 w-5 sm:h-6 sm:w-6", scrolled ? "text-black" : "text-white")} />
                ) : (
                  <Menu className={cn("h-5 w-5 sm:h-6 sm:w-6", scrolled ? "text-black" : "text-white")} />
                )}
              </button>
            </div>

            {/* Right Side Actions */}
            {/* CHANGELOG: acciones derecha visible en md+ para permitir el botón de WhatsApp en tablet/laptop */}
            <div className="hidden md:flex items-center justify-end h-full gap-0.5 xl:gap-1 min-w-0 flex-shrink-0 flex-none">
              {/* Tipo de Cambio (first) */}
              <a
                href="/divisas"
                className={cn(
                  "hidden sm:inline-flex items-center gap-1 px-1.5 lg:px-2 xl:px-3 py-1 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 min-w-[64px] z-10",
                  scrolled ? "text-gray-900 hover:text-[#fb0009] hover:bg-[#fb0009]/5 focus-visible:ring-[#fb0009]" : "text-white hover:text-[#fb0009] hover:bg-white/10 focus-visible:ring-white"
                )}
                aria-label={t('nav_divisas')}
                title={t('nav_divisas')}
                data-ga-action="nav_click"
                data-ga-category="navigation"
                data-ga-label="divisas"
              >
                <span
                  className={cn(
                    "inline-flex h-5 w-5 sm:h-5 sm:w-5 items-center justify-center rounded-full text-[11px] sm:text-[12px] leading-[18px] sm:leading-[18px] border transition-colors",
                    scrolled
                      ? "border-gray-300/90 text-gray-900 bg-transparent group-hover:border-[#fb0009] group-hover:text-[#fb0009]"
                      : "border-white/80 text-white bg-white/5 group-hover:border-[#fb0009] group-hover:text-[#fb0009]"
                  )}
                  aria-hidden="true"
                >
                  $
                </span>
                <span className="hidden xl:inline tracking-tight">{t('nav_divisas')}</span>
              </a>
              {/* Pagos en línea - Dropdown */}
              <div className="hidden sm:inline-block relative pagos-dropdown">
                <button
                  className={cn(
                    "inline-flex items-center gap-1 px-1.5 lg:px-2 xl:px-3 py-1 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 min-w-[64px] z-10",
                    scrolled ? "text-gray-900 hover:text-[#fb0009] hover:bg-[#fb0009]/5 focus-visible:ring-[#fb0009]" : "text-white hover:text-[#fb0009] hover:bg-white/10 focus-visible:ring-white"
                  )}
                  onClick={() => setPagosMenuOpen(prev => !prev)}
                  aria-label={t('nav_pagos')}
                  title={t('nav_pagos')}
                  data-ga-action="pagos_toggle"
                  data-ga-category="navigation"
                  data-ga-label="pagos"
                >
                  <CreditCard
                    aria-hidden="true"
                    className={cn(
                      "w-5 h-5 transition-colors",
                      scrolled ? "text-gray-900 group-hover:text-[#fb0009]" : "text-white/90 group-hover:text-[#fb0009]"
                    )}
                  />
                  <span className="hidden xl:inline tracking-tight">{t('nav_pagos')}</span>
                </button>
                {pagosMenuOpen && (
                  <div className={cn("absolute right-0 mt-2 w-48 rounded-xl bg-white border shadow-lg z-[10000]")}>
                    <ul className="p-2">
                      <li>
                        <a
                          href="/pagos_mx"
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 text-sm text-gray-700"
                          onClick={() => setPagosMenuOpen(false)}
                        >
                          <span className="text-lg">🇲🇽</span>
                          <span>México</span>
                        </a>
                      </li>
                      <li>
                        <a
                          href="/pagos_canada"
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 text-sm text-gray-700"
                          onClick={() => setPagosMenuOpen(false)}
                        >
                          <span className="text-lg">🇨🇦</span>
                          <span>Canadá</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
              </div>


              {/* Social (Desktop) */}
              <span className={cn(
                "hidden xl:inline-block h-5 w-px mx-1",
                scrolled ? "bg-gray-200/80" : "bg-white/20"
              )} aria-hidden="true" />
              {socialLinks.map(({ href, label, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(`social_${label.toLowerCase()}`) ?? label}
                  title={t(`social_${label.toLowerCase()}`) ?? label}
                  className={cn(
                    "hidden xl:inline-flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    scrolled ? "border-gray-200 text-gray-700 hover:text-[#fb0009] hover:border-[#fb0009]/30 hover:bg-gray-100 focus-visible:ring-[#fb0009]" : "border-white/20 text-white/90 hover:text-[#fb0009] hover:border-[#fb0009]/40 hover:bg-white/10 focus-visible:ring-white"
                  )}
                  data-ga-action="social_click"
                  data-ga-category="social"
                  data-ga-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}

              <span className={cn(
                "hidden xl:inline-block h-5 w-px mx-1 hide-below-17in",
                scrolled ? "bg-gray-200/80" : "bg-white/20"
              )} aria-hidden="true" />

              {/* Language selector (desktop) */}
              <div className="hidden sm:inline-block relative lang-dropdown">
                <button
                  className={cn(
                    "inline-flex items-center gap-1 px-1.5 lg:px-2 xl:px-3 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors",
                    scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white/90 hover:bg-white/10"
                  )}
                  onClick={() => setLangMenuOpen(prev => !prev)}
                  data-ga-action="lang_toggle"
                  data-ga-category="i18n"
                  data-ga-label={lang}
                >
                  <Languages className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                  <span>{langLabel(lang)}</span>
                </button>
                {langMenuOpen && (
                  <div className={cn("absolute right-0 mt-2 w-40 rounded-xl bg-white border shadow-lg z-[10000]")}>
                    <ul className="p-2">
                      {(['ES', 'EN', 'FR'] as const).map((L) => (
                        <li key={L}>
                          <button
                            className={cn("w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 text-sm", lang === L ? 'font-semibold text-[#fb0009]' : 'text-gray-700')}
                            onClick={() => { setLang(L as any); setLangMenuOpen(false) }}
                          >{L === 'ES' ? 'Español' : L === 'EN' ? 'English' : 'Français'}</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* mobile button moved above so it's visible on small screens */}
            </div>
          </div>
        </div>

        {/* Mobile Menu Drawer + Overlay */}
        {isMenuOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={handleMenuToggle} />
            <div className="fixed top-0 right-0 z-50 h-screen w-[90%] max-w-sm bg-white shadow-2xl border-l border-gray-200 flex flex-col">
              <div className="flex items-center justify-between h-14 sm:h-16 px-4 border-b">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Image src="/logos/Logo_white.png" alt={t('brand_alt')} className="h-auto w-auto sm:h-auto sm:w-auto" width={160} height={40} priority />
                </div>
                <button className="p-2 rounded-lg hover:bg-gray-100" onClick={handleMenuToggle} aria-label="Cerrar menú">
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
              <div className="p-3 sm:p-4 overflow-y-auto flex-1">
                <div className="space-y-1 sm:space-y-2 mb-4 sm:mb-6">
                  {mainNavItems.map((item) => (
                    <div key={item.name}>
                      {item.submenu ? (
                        <div>
                          <button
                            onClick={() => handleDropdownToggle(item.name)}
                            className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-xl hover:bg-[#fb0009]/5 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <item.icon className="w-5 h-5 text-gray-600 group-hover:text-[#fb0009] transition-colors" />
                              <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                                {item.name}
                              </span>
                            </div>
                            <ChevronDown className={cn(
                              "w-4 h-4 text-gray-400 transition-transform duration-300",
                              activeDropdown === item.name ? "rotate-180" : ""
                            )} />
                          </button>
                          {activeDropdown === item.name && (
                            <div className="mt-2 ml-1 space-y-2">
                              {(item.submenu as any[]).map((subItem: any, index: number) => (
                                <div key={index} className="rounded-xl overflow-hidden border border-gray-100">
                                  {(() => {
                                    const subHref = subItem.externalHref ?? subItem.href
                                    const isExternalSubLink = Boolean(subItem.externalHref)
                                    return (
                                      <>
                                  {subItem.children ? (
                                    <>
                                      <button
                                        onClick={() => setActiveServiciosSub(activeServiciosSub === subItem.name ? null : subItem.name)}
                                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-[#fb0009]/5 transition-colors"
                                        data-ga-action="nav_expand"
                                        data-ga-category="navigation"
                                        data-ga-label={`submenu:${subItem.name}`}
                                      >
                                        <div className="flex items-center gap-3">
                                          {subItem.icon && <span className="text-sm">{subItem.icon}</span>}
                                          <span className="text-sm font-semibold text-gray-800">{subItem.name}</span>
                                        </div>
                                        <ChevronDown className={cn("w-4 h-4 text-gray-500 transition-transform", activeServiciosSub === subItem.name ? "rotate-180" : "")} />
                                      </button>
                                      {activeServiciosSub === subItem.name && (
                                        (() => {
                                          let mobileChildren = subItem.children as any[]
                                          // Agregar solo en mobile los nuevos productos relacionados a inmigración.
                                          if (subItem.href === '/servicios/lets-inmigrate') {
                                            const extras = [
                                              { name: 'ETA UK', href: '/servicios/eta-uk' },
                                              { name: 'ETA Canadá', href: '/servicios/eta-canadiense' },
                                              { name: 'Migración a Canadá', href: '/servicios/lets-inmigrate' }
                                            ]
                                            const existing = new Set(mobileChildren.map(c => c.href))
                                            for (const e of extras) {
                                              if (!existing.has(e.href)) mobileChildren = [...mobileChildren, e]
                                            }
                                          }
                                          return (
                                            <div className="px-3 pb-3 pt-1 bg-white grid grid-cols-1 gap-1">
                                              {mobileChildren.map((opt: any, i: number) => (
                                                <a
                                                  key={i}
                                                  href={opt.href}
                                                  className="block p-2 rounded-lg hover:bg-[#fb0009]/5 text-sm text-gray-700"
                                                  onClick={() => setIsMenuOpen(false)}
                                                  data-ga-action="nav_click"
                                                  data-ga-category="navigation"
                                                  data-ga-label={`submenu_option:${opt.name}`}
                                                >
                                                  {opt.name}
                                                </a>
                                              ))}
                                            </div>
                                          )
                                        })()
                                      )}
                                    </>
                                  ) : (
                                    <a
                                      href={subHref}
                                      target={isExternalSubLink ? "_blank" : undefined}
                                      rel={isExternalSubLink ? "noopener noreferrer" : undefined}
                                      className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-[#fb0009]/5 transition-colors"
                                      onClick={() => setIsMenuOpen(false)}
                                      data-ga-action="nav_click"
                                      data-ga-category="navigation"
                                      data-ga-label={`submenu:${subItem.name}`}
                                    >
                                      {subItem.icon && <span className="text-sm">{subItem.icon}</span>}
                                      <span className="text-sm font-semibold text-gray-800">{subItem.name}</span>
                                    </a>
                                  )}
                                      </>
                                    )
                                  })()}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          href={item.href}
                          className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-[#fb0009]/5 transition-colors group"
                          onClick={() => setIsMenuOpen(false)}
                          data-ga-action="nav_click"
                          data-ga-category="navigation"
                          data-ga-label={item.name}
                        >
                          <item.icon className="w-5 h-5 text-gray-600 group-hover:text-[#fb0009] transition-colors" />
                          <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                            {item.name}
                          </span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-3 sm:p-4 border-t space-y-2 sm:space-y-3">
                {/* Social (Mobile) */}
                <div className="flex items-center justify-center gap-1 sm:gap-2 pb-1">
                  {socialLinks.map(({ href, label, icon: Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t(`social_${label.toLowerCase()}`) ?? label}
                      title={t(`social_${label.toLowerCase()}`) ?? label}
                      className="p-2 rounded-lg text-gray-600 hover:text-[#fb0009] hover:bg-[#fb0009]/5 transition-colors"
                      data-ga-action="social_click"
                      data-ga-category="social"
                      data-ga-label={label}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  ))}
                </div>

                {/* Tipo de Cambio (Mobile) */}
                <a
                  href="/divisas"
                  className="w-full border-2 border-[#fb0009] text-[#fb0009] py-3 sm:py-4 rounded-xl font-bold hover:bg-[#fb0009] hover:text-white transition-colors flex items-center justify-center gap-2"
                  data-ga-action="nav_click"
                  data-ga-category="navigation"
                  data-ga-label="divisas_mobile"
                >
                  <CircleDollarSign className="w-5 h-5 sm:w-6 sm:h-6" />
                  {t('nav_divisas')}
                </a>

                {/* Pagos (Mobile) - Dropdown */}
                <div className="w-full space-y-2">
                  <button
                    onClick={() => setPagosMenuOpen(prev => !prev)}
                    className="w-full border-2 border-[#fb0009] text-[#fb0009] py-3 sm:py-4 rounded-xl font-bold hover:bg-[#fb0009] hover:text-white transition-colors flex items-center justify-center gap-2"
                    data-ga-action="pagos_toggle"
                    data-ga-category="navigation"
                    data-ga-label="pagos_mobile"
                  >
                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
                    {t('nav_pagos')}
                    <ChevronDown className={cn("w-4 h-4 transition-transform", pagosMenuOpen ? "rotate-180" : "")} />
                  </button>
                  {pagosMenuOpen && (
                    <div className="space-y-2 pl-4">
                      <a
                        href="/pagos_mx"
                        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => { setPagosMenuOpen(false); setIsMenuOpen(false) }}
                      >
                        <span className="text-2xl">🇲🇽</span>
                        <span className="font-semibold text-gray-800">México</span>
                      </a>
                      <a
                        href="/pagos_canada"
                        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => { setPagosMenuOpen(false); setIsMenuOpen(false) }}
                      >
                        <span className="text-2xl">🇨🇦</span>
                        <span className="font-semibold text-gray-800">Canadá</span>
                      </a>
                    </div>
                  )}
                </div>

                {/* Language toggle (Mobile) */}
                <div className="w-full">
                  <label htmlFor="mobile-lang" className="sr-only">{t('lang_toggle_aria')}</label>
                  <select
                    id="mobile-lang"
                    className="w-full p-3 rounded-xl border border-gray-200 text-gray-700"
                    onChange={(e) => { setLang(e.target.value as any); setIsMenuOpen(false) }}
                    value={lang}
                    aria-label={t('lang_toggle_aria')}
                  >
                    <option value="ES">Español</option>
                    <option value="EN">English</option>
                    <option value="FR">Français</option>
                  </select>
                </div>

                <button
                  className="w-full border-2 border-[#fb0009] text-[#fb0009] py-3 sm:py-4 rounded-xl font-bold hover:bg-[#fb0009] hover:text-white transition-colors flex items-center justify-center gap-2"
                  data-ga-action="cta_click"
                  data-ga-category="cta"
                  data-ga-label={`${t('llamar_ahora')}_mobile`}
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  {t('llamar_ahora')}
                </button>
                <br />
                <br />
                <br />
              </div>
            </div>
          </>
        )}


      </header>

      <main>
        <br />
        <br />
      </main>

      {/* Estilos */}
      <style jsx>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        :root {
          --primary: #fb0009;
        }
        
        .text-primary {
          color: var(--primary);
        }
        
        .bg-primary {
          background-color: var(--primary);
        }
        
        .border-primary {
          border-color: var(--primary);
        }
        
        .hover\\:bg-primary:hover {
          background-color: var(--primary);
        }
        
        .hover\\:text-primary:hover {
          color: var(--primary);
        }
        
        .from-primary {
          --tw-gradient-from: var(--primary);
        }
        
        .to-primary\\/80 {
          --tw-gradient-to: color-mix(in srgb, var(--primary) 80%, transparent);
        }
        /* Visibility rules for 17in (~1632px) breakpoint */
        .cta-visible { display: none !important; }
        .whatsapp-for-small { display: none !important; }

        @media (min-width: 1632px) {
          .cta-visible { display: inline-flex !important; }
          .whatsapp-for-small { display: none !important; }
        }

        @media (max-width: 1631px) {
          .cta-visible { display: none !important; }
          .whatsapp-for-small { display: inline-flex !important; }
        }
        .hide-below-17in { display: inline-block !important; }
        @media (max-width: 1631px) {
          .hide-below-17in { display: none !important; }
        }
      `}</style>
    </>

  )

}
